/**
 * Cash-up (X / Z) — on-screen replica of the till's own X and Z reports.
 *
 * X = one user's shift (scope: transactions.shift_id).
 * Z = a whole till session (scope: transactions.device_session_id).
 * Data comes from /api/inventory/reports/cashup/* which mirrors the POS
 * `_calculateTransactionSummary` maths, so the numbers match the printout.
 */

import React, { useEffect, useRef, useState } from 'react';
import html2pdf from 'html2pdf.js';
import { getLocations } from '../../services/inventoryCorrections.service';
import {
  CashupReportData,
  CashupSessionOption,
  CashupShiftOption,
  getCashupDay,
  getXReport,
  getZReport,
} from '../../services/cashupReport.service';
import { webNavigate } from '../../services/navigate.service';
import { useResponsive } from '../../hook/useResponsive';
import { useLanguage } from './shared/useLanguage';

const TEAL = '#0F7679';
const INK = '#222525';
const MUTED = '#4A5353';
const CARD_BORDER = '#EBF0F0';
const INPUT_BORDER = '#DBE4E4';
const GOOD_BG = '#ECFDF3';
const GOOD_FG = '#0E8B53';
const WARN_BG = '#FFFBEB';
const WARN_FG = '#92400E';
const BAD_BG = '#FEF2F2';
const BAD_FG = '#B91C1C';
const MONO = "ui-monospace, 'JetBrains Mono', Menlo, Consolas, monospace";

const formatMoney = (value: number) =>
  new Intl.NumberFormat('en-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

// 20.00 -> "20%", 8.50 -> "8.5%".
const formatRate = (rate: number) => `${Number(rate).toFixed(2).replace(/\.?0+$/, '')}%`;

const formatTime = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`;
};

const todayYyyyMmDd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
};

type Mode = 'x' | 'z';

interface Location {
  id: string;
  locationName?: string;
  name?: string;
}

const CashupXZReport: React.FC = () => {
  const { t } = useLanguage();
  const { isMobile } = useResponsive();
  const companyId = localStorage.getItem('companyId') || '';
  const userId = localStorage.getItem('userId') || '';
  const languagePrefix = localStorage.getItem('language') || 'en';

  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState('');
  const [date, setDate] = useState(todayYyyyMmDd());
  const [mode, setMode] = useState<Mode>('x');
  const [shifts, setShifts] = useState<CashupShiftOption[]>([]);
  const [sessions, setSessions] = useState<CashupSessionOption[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [report, setReport] = useState<CashupReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const receiptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await getLocations(userId, companyId, 1, 200, '');
        const list =
          res?.data?.data?.locations ??
          res?.data?.locations ??
          res?.data?.data ??
          [];
        if (!cancelled && Array.isArray(list)) setLocations(list);
      } catch {
        // ignored — "All locations" still works
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, userId]);

  // Day pickers: shifts feed the X select, sessions the Z one. Open first.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await getCashupDay(date, locationId || undefined);
        if (cancelled) return;
        const data = res?.data?.data ?? {};
        const openFirst = <T extends { open: boolean }>(rows: T[]) =>
          [...rows].sort((a, b) => Number(b.open) - Number(a.open));
        setShifts(openFirst(Array.isArray(data.shifts) ? data.shifts : []));
        setSessions(
          openFirst(Array.isArray(data.sessions) ? data.sessions : []),
        );
      } catch (dayError) {
        console.error('Error loading cashup day:', dayError);
        if (!cancelled) {
          setShifts([]);
          setSessions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date, locationId]);

  // Keep the selection valid whenever the mode or the day's options change.
  useEffect(() => {
    const options: any[] = mode === 'x' ? shifts : sessions;
    const key = mode === 'x' ? 'userShiftId' : 'deviceSessionId';
    if (!options.some((o) => o[key] === selectedId)) {
      setSelectedId(options.length ? options[0][key] : '');
    }
  }, [mode, shifts, sessions]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedId) {
        setReport(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res: any =
          mode === 'x'
            ? await getXReport(selectedId)
            : await getZReport(selectedId);
        if (!cancelled) setReport(res?.data?.data ?? null);
      } catch (reportError) {
        console.error('Error loading cashup report:', reportError);
        if (!cancelled) {
          setReport(null);
          setError(
            t(
              'Unable to load the report',
              'Unable to load the report. Please try again.',
            ),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, selectedId]);

  const exportPdf = () => {
    const node = receiptRef.current;
    if (!node || !report) return;
    const who = (
      (mode === 'x' ? report.userName : report.deviceName) || mode
    ).replace(/\s+/g, '-');
    // The receipt node is in-flow; capturing an absolute wrapper renders blank.
    html2pdf()
      .set({
        margin: 10,
        filename: `${mode}-report_${who}_${date}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(node)
      .save();
  };

  const fresh =
    !!report &&
    report.freshness.pendingTotal === 0 &&
    report.freshness.staleTills === 0;

  const shiftLabel = (s: CashupShiftOption) =>
    `${s.userName}${s.deviceName ? ` · ${s.deviceName}` : ''} · ${formatTime(
      s.startDate,
    )}${s.open ? ` — ${t('open', 'open')}` : ` → ${formatTime(s.endDate)}`}`;
  const sessionLabel = (s: CashupSessionOption) =>
    `${s.deviceName ?? t('Till', 'Till')}${
      s.sessionNumber ? ` · #${s.sessionNumber}` : ''
    } · ${formatTime(s.startDate)}${
      s.open ? ` — ${t('open', 'open')}` : ` → ${formatTime(s.endDate)}`
    }`;

  const inputStyle: React.CSSProperties = {
    height: 44,
    borderRadius: 10,
    border: `1px solid ${INPUT_BORDER}`,
    padding: '0 12px',
    background: '#FFFFFF',
    color: INK,
    fontSize: 14,
    fontFamily: 'inherit',
    outline: 'none',
    width: '100%',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    color: MUTED,
    fontWeight: 500,
    letterSpacing: '0.02em',
  };
  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  };
  const focusTeal = {
    onFocus: (e: any) => (e.target.style.borderColor = TEAL),
    onBlur: (e: any) => (e.target.style.borderColor = INPUT_BORDER),
  };

  const receiptRow = (
    label: React.ReactNode,
    value: React.ReactNode,
    bold = false,
  ) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        fontVariantNumeric: 'tabular-nums',
        fontWeight: bold ? 700 : 400,
      }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
  const dashedRule = (
    <hr
      style={{ border: 0, borderTop: '1px dashed #B9C6C6', margin: '12px 0' }}
    />
  );
  const sect = (label: string) => (
    <p
      style={{
        fontWeight: 700,
        letterSpacing: '0.14em',
        fontSize: 11.5,
        margin: '2px 0 6px',
      }}
    >
      {label}
    </p>
  );
  // VAT in the UK, TAX elsewhere, as the API says; an API without the label keeps the old VAT wording.
  const isVat = report?.taxLabel !== 'TAX';
  const taxRows = report?.taxBreakdown ?? [];

  return (
    <div
      className="h-full"
      style={{
        overflowY: 'auto',
        padding: isMobile ? '4px 0 80px' : '4px 0 32px',
      }}
    >
      {/* Breadcrumb */}
      <div
        className="flex items-center"
        style={{ gap: 8, marginBottom: 8, flexWrap: 'wrap' }}
      >
        <button
          type="button"
          onClick={() => webNavigate(`/${languagePrefix}/pos-reports`)}
          className="font-inter"
          style={{
            color: MUTED,
            fontSize: 13,
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = TEAL)}
          onMouseLeave={(e) => (e.currentTarget.style.color = MUTED)}
        >
          ← {t('POS Reports', 'POS Reports')}
        </button>
        <span aria-hidden style={{ color: '#9CA3AF' }}>
          /
        </span>
        <span
          className="font-inter"
          style={{ color: INK, fontSize: 13, fontWeight: 500 }}
        >
          {t('Cash-up (X / Z)', 'Cash-up (X / Z)')}
        </span>
      </div>

      {/* Title block */}
      <p
        className="font-inter"
        style={{
          fontSize: isMobile ? '20px' : '24px',
          fontWeight: 500,
          color: INK,
          lineHeight: 1.2,
        }}
      >
        {t('Cash-up (X / Z)', 'Cash-up (X / Z)')}
      </p>
      <p
        className="font-inter"
        style={{
          marginTop: 6,
          fontSize: isMobile ? '13px' : '14px',
          color: MUTED,
          lineHeight: 1.5,
          maxWidth: 720,
        }}
      >
        {t(
          'The same report the till prints — for a manager who is not on site.',
          'The same report the till prints — for a manager who is not on site.',
        )}
      </p>

      {/* Controls card */}
      <div
        className="font-inter"
        style={{
          marginTop: isMobile ? 20 : 28,
          padding: isMobile ? '16px' : '20px 24px',
          borderRadius: 12,
          border: `1px solid ${CARD_BORDER}`,
          background: '#FFFFFF',
          boxShadow: '0 1px 0 rgba(15, 41, 42, 0.02)',
          display: 'grid',
          gridTemplateColumns: isMobile
            ? '1fr 1fr'
            : 'minmax(160px, 220px) minmax(140px, 180px) auto minmax(220px, 320px) auto',
          gap: 12,
          alignItems: 'end',
        }}
      >
        <div style={fieldStyle}>
          <label className="font-inter" style={labelStyle}>
            {t('Location', 'Location')}
          </label>
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            className="font-inter"
            style={inputStyle}
            {...focusTeal}
          >
            <option value="">{t('All locations', 'All locations')}</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.locationName ?? loc.name ?? loc.id}
              </option>
            ))}
          </select>
        </div>
        <div style={fieldStyle}>
          <label className="font-inter" style={labelStyle}>
            {t('Day', 'Day')}
          </label>
          <input
            type="date"
            value={date}
            max={todayYyyyMmDd()}
            onChange={(e) => setDate(e.target.value)}
            className="font-inter"
            style={inputStyle}
            {...focusTeal}
          />
        </div>
        <div
          style={{
            ...fieldStyle,
            gridColumn: isMobile ? '1 / span 2' : 'auto',
          }}
        >
          <label className="font-inter" style={labelStyle}>
            {t('Report', 'Report')}
          </label>
          <div
            role="tablist"
            aria-label={t('Report', 'Report')}
            style={{ display: 'flex', gap: 8 }}
          >
            {(['x', 'z'] as Mode[]).map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={mode === value}
                onClick={() => setMode(value)}
                className="font-inter"
                style={{
                  flex: isMobile ? 1 : '0 0 auto',
                  height: 44,
                  minWidth: 96,
                  padding: '0 14px',
                  borderRadius: 10,
                  border: `1px solid ${mode === value ? TEAL : INPUT_BORDER}`,
                  background: mode === value ? '#E7F1F1' : '#FFFFFF',
                  color: mode === value ? TEAL : INK,
                  fontWeight: mode === value ? 600 : 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  transition: 'all 120ms ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {value === 'x'
                  ? `X · ${t('one user', 'one user')}`
                  : `Z · ${t('whole till', 'whole till')}`}
              </button>
            ))}
          </div>
        </div>
        <div
          style={{
            ...fieldStyle,
            gridColumn: isMobile ? '1 / span 2' : 'auto',
          }}
        >
          <label className="font-inter" style={labelStyle}>
            {mode === 'x'
              ? t('User shift', 'User shift')
              : t('Till session', 'Till session')}
          </label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="font-inter"
            style={inputStyle}
            {...focusTeal}
          >
            {mode === 'x' ? (
              shifts.length === 0 ? (
                <option value="">
                  {t('No shifts on this day', 'No shifts on this day')}
                </option>
              ) : (
                shifts.map((s) => (
                  <option key={s.userShiftId} value={s.userShiftId}>
                    {shiftLabel(s)}
                  </option>
                ))
              )
            ) : sessions.length === 0 ? (
              <option value="">
                {t('No sessions on this day', 'No sessions on this day')}
              </option>
            ) : (
              sessions.map((s) => (
                <option key={s.deviceSessionId} value={s.deviceSessionId}>
                  {sessionLabel(s)}
                </option>
              ))
            )}
          </select>
        </div>
        <button
          type="button"
          onClick={exportPdf}
          disabled={!report || loading}
          className="font-inter"
          style={{
            gridColumn: isMobile ? '1 / span 2' : 'auto',
            backgroundColor: !report || loading ? '#9CA3AF' : TEAL,
            color: '#FFFFFF',
            fontWeight: 600,
            fontSize: 14,
            height: 44,
            padding: '0 20px',
            borderRadius: 10,
            border: 'none',
            cursor: !report || loading ? 'not-allowed' : 'pointer',
            transition: 'background-color 160ms ease',
            whiteSpace: 'nowrap',
          }}
        >
          {t('Export PDF', 'Export PDF')}
        </button>
      </div>

      {error && (
        <div
          role="alert"
          className="font-inter"
          style={{
            marginTop: 16,
            fontSize: 13,
            color: BAD_FG,
            background: BAD_BG,
            border: '1px solid #FECACA',
            padding: '8px 12px',
            borderRadius: 8,
          }}
        >
          {error}
        </div>
      )}

      {/* Freshness banner */}
      {report && (
        <div
          className="font-inter"
          style={{
            marginTop: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            borderRadius: 10,
            padding: '10px 14px',
            background: fresh ? GOOD_BG : WARN_BG,
            border: `1px solid ${fresh ? '#BBE5CB' : '#FDE68A'}`,
            color: fresh ? GOOD_FG : WARN_FG,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              backgroundColor: 'currentColor',
              flex: 'none',
            }}
          />
          {fresh
            ? t(
                'All tills synced — figures are complete',
                'All tills synced — figures are complete',
              )
            : `${report.freshness.pendingTotal} ${t(
                'unsent transactions',
                'unsent transactions',
              )} · ${report.freshness.staleTills} ${t(
                'tills not reporting — totals may be behind',
                'tills not reporting — totals may be behind',
              )}`}
        </div>
      )}

      {/* Body: receipt + side rail */}
      <div
        style={{
          marginTop: 16,
          display: 'grid',
          gap: isMobile ? 16 : 24,
          gridTemplateColumns: isMobile
            ? 'minmax(0, 1fr)'
            : 'minmax(0, 440px) minmax(240px, 340px)',
          alignItems: 'start',
        }}
      >
        <div>
          {loading ? (
            <div
              className="cashup-pulse"
              style={{ height: 420, borderRadius: 12, background: '#EEF3F4' }}
            />
          ) : !report ? (
            <div
              className="font-inter"
              style={{
                padding: '48px 16px',
                textAlign: 'center',
                color: MUTED,
                background: '#FFFFFF',
                border: `1px dashed ${INPUT_BORDER}`,
                borderRadius: 12,
                fontSize: 13,
              }}
            >
              {t(
                'Pick a shift or session to see its report',
                'Pick a shift or session to see its report',
              )}
            </div>
          ) : (
            <div ref={receiptRef}>
              <div
                style={{
                  background: '#FFFFFF',
                  border: `1px solid ${CARD_BORDER}`,
                  borderBottom: 'none',
                  borderRadius: '6px 6px 0 0',
                  boxShadow: '0 1px 0 rgba(15, 41, 42, 0.02)',
                  padding: isMobile ? '20px 18px' : '26px',
                  fontFamily: MONO,
                  fontSize: 12.5,
                  lineHeight: 1.55,
                  color: INK,
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: 15, fontWeight: 700 }}>
                    {report.deviceName ?? ''}
                  </p>
                  <p
                    style={{
                      fontWeight: 700,
                      fontSize: 13,
                      letterSpacing: '0.24em',
                      marginTop: 8,
                    }}
                  >
                    {mode === 'x' ? '— X REPORT —' : '— Z REPORT —'}
                  </p>
                  <p>
                    {mode === 'x'
                      ? `${report.userName ?? ''} · ${formatTime(report.startDate)}`
                      : `#${report.sessionNumber ?? ''} · ${formatTime(report.startDate)}`}
                    {report.open
                      ? ` → ${t('now', 'now')}`
                      : ` → ${formatTime(report.endDate)}`}
                  </p>
                  <p style={{ color: MUTED }}>
                    {date} ·{' '}
                    {t(
                      'generated from the dashboard',
                      'generated from the dashboard',
                    )}
                    {fresh ? ' · ✓' : ` · ${t('sync pending', 'sync pending')}`}
                  </p>
                </div>

                {dashedRule}
                {sect(t('SALES SUMMARY', 'SALES SUMMARY'))}
                {receiptRow(
                  t('Total sales', 'Total sales'),
                  formatMoney(report.summary.totalSales),
                )}
                {receiptRow(
                  t('Discounts', 'Discounts'),
                  formatMoney(-Math.abs(report.summary.totalDiscounts)),
                )}
                {receiptRow(
                  t('Refunds', 'Refunds'),
                  formatMoney(report.summary.totalRefunds),
                )}
                {receiptRow(
                  t('Voids', 'Voids'),
                  formatMoney(report.summary.totalVoids),
                )}
                {receiptRow(
                  isVat
                    ? t('Net sales (ex VAT)', 'Net sales (ex VAT)')
                    : t('Net sales (ex TAX)', 'Net sales (ex TAX)'),
                  formatMoney(report.summary.netSales),
                  true,
                )}

                {report.taxBreakdown && (
                  <>
                    {dashedRule}
                    {sect(isVat ? t('VAT BREAKDOWN', 'VAT BREAKDOWN') : t('TAX BREAKDOWN', 'TAX BREAKDOWN'))}
                    {taxRows.length === 0 ? (
                      <p style={{ color: MUTED }}>
                        {isVat
                          ? t('No VAT in this report', 'No VAT in this report')
                          : t('No TAX in this report', 'No TAX in this report')}
                      </p>
                    ) : (
                      taxRows.map((row) => (
                        <React.Fragment key={row.taxId ?? row.code}>
                          {receiptRow(
                            `${row.taxId ? row.name || row.code : t('Tip', 'Tip')}${row.rate !== null ? ` ${formatRate(row.rate)}` : ''}`,
                            formatMoney(row.tax),
                          )}
                          {receiptRow(
                            <span style={{ color: MUTED, paddingLeft: 12 }}>{t('Taxable', 'Taxable')}</span>,
                            <span style={{ color: MUTED }}>{formatMoney(row.taxable)}</span>,
                          )}
                        </React.Fragment>
                      ))
                    )}
                    {receiptRow(
                      isVat ? t('Total VAT', 'Total VAT') : t('Total TAX', 'Total TAX'),
                      formatMoney(report.summary.totalTax ?? 0),
                      true,
                    )}

                    {dashedRule}
                    {sect(t('SERVICE CHARGE & TIP', 'SERVICE CHARGE & TIP'))}
                    {receiptRow(
                      t('Service charge', 'Service charge'),
                      formatMoney(report.summary.serviceCharge ?? 0),
                    )}
                    {receiptRow(t('Tip', 'Tip'), formatMoney(report.summary.tip ?? 0))}
                  </>
                )}

                {dashedRule}
                {sect(t('TENDERS', 'TENDERS'))}
                {report.tenders.length === 0 ? (
                  <p style={{ color: MUTED }}>
                    {t('No payments yet', 'No payments yet')}
                  </p>
                ) : (
                  report.tenders.map((tender) => (
                    <React.Fragment
                      key={tender.deviceTenderId ?? tender.tenderName}
                    >
                      {receiptRow(
                        tender.tenderName,
                        formatMoney(tender.amount),
                      )}
                    </React.Fragment>
                  ))
                )}

                {dashedRule}
                {sect(t('CASH DECLARATION', 'CASH DECLARATION'))}
                {receiptRow(
                  t('Float', 'Float'),
                  formatMoney(report.cashDeclaration.float),
                )}
                {receiptRow(
                  t('Cash movement', 'Cash movement'),
                  formatMoney(report.cashDeclaration.cashMovement),
                )}
                <div
                  style={{
                    backgroundColor: 'rgba(15,118,121,0.07)',
                    border: '1px solid rgba(15,118,121,0.35)',
                    borderRadius: 8,
                    padding: '10px 12px',
                    margin: '12px 0 2px',
                  }}
                >
                  <p
                    style={{
                      fontSize: 10.5,
                      color: TEAL,
                      letterSpacing: '0.08em',
                      fontWeight: 600,
                      marginBottom: 3,
                    }}
                  >
                    {report.open
                      ? t('CASH TO DECLARE', 'CASH TO DECLARE')
                      : t('DECLARED AT CLOSE', 'DECLARED AT CLOSE')}
                  </p>
                  {receiptRow(
                    t('System amount', 'System amount'),
                    formatMoney(report.cashDeclaration.systemAmount),
                    true,
                  )}
                  {report.cashDeclaration.declaredAmount !== null &&
                    receiptRow(
                      t('Declared', 'Declared'),
                      formatMoney(report.cashDeclaration.declaredAmount),
                      true,
                    )}
                  {report.cashDeclaration.variance !== null && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color:
                          Math.abs(report.cashDeclaration.variance) < 0.005
                            ? GOOD_FG
                            : BAD_FG,
                      }}
                    >
                      <span>{t('Variance', 'Variance')}</span>
                      <span>
                        {formatMoney(report.cashDeclaration.variance)}
                      </span>
                    </div>
                  )}
                </div>
                {report.open && (
                  <p
                    style={{ color: MUTED, fontSize: 11, textAlign: 'center' }}
                  >
                    {t(
                      'declared & variance are filled in when the shift closes',
                      'declared & variance are filled in when the shift closes',
                    )}
                  </p>
                )}

                {report.declarations.length > 0 && (
                  <>
                    {dashedRule}
                    {sect(
                      t('DECLARATIONS BY TENDER', 'DECLARATIONS BY TENDER'),
                    )}
                    {report.declarations.map((row) => (
                      <React.Fragment key={row.tenderName}>
                        {receiptRow(
                          row.tenderName,
                          `${formatMoney(row.systemAmount)} / ${formatMoney(row.declaredAmount)}`,
                        )}
                      </React.Fragment>
                    ))}
                  </>
                )}

                {mode === 'z' &&
                  ((report.byUser && report.byUser.length > 0) ||
                    Math.abs(report.unassignedSales ?? 0) > 0.005) && (
                    <>
                      {dashedRule}
                      {sect(t('BY USER', 'BY USER'))}
                      {(report.byUser ?? []).map((row) => (
                        <React.Fragment key={row.userShiftId}>
                          {receiptRow(
                            row.userName,
                            formatMoney(row.totalSales),
                          )}
                        </React.Fragment>
                      ))}
                      {Math.abs(report.unassignedSales ?? 0) > 0.005 &&
                        receiptRow(
                          t('Unassigned', 'Unassigned'),
                          formatMoney(report.unassignedSales ?? 0),
                        )}
                    </>
                  )}

                {dashedRule}
                {sect(t('STATS', 'STATS'))}
                {receiptRow(
                  t('Transactions', 'Transactions'),
                  String(report.summary.txnCount),
                )}
                {receiptRow(
                  t('Items sold', 'Items sold'),
                  String(report.summary.itemsSold),
                )}
                {receiptRow(
                  t('Avg / transaction', 'Avg / transaction'),
                  formatMoney(report.summary.avgPerTxn),
                )}
                {report.summary.covers !== undefined &&
                  receiptRow(
                    t('Total covers', 'Total covers'),
                    String(report.summary.covers),
                  )}
              </div>
              {/* Torn receipt edge */}
              <div aria-hidden className="cashup-tear" />
            </div>
          )}
        </div>

        {/* Side rail */}
        {report && !loading && (
          <div
            className="font-inter"
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            {report.parity && (
              <div
                style={{
                  borderRadius: 10,
                  padding: '10px 14px',
                  background: report.parity.matches ? GOOD_BG : BAD_BG,
                  border: `1px solid ${
                    report.parity.matches ? '#BBE5CB' : '#FECACA'
                  }`,
                  color: report.parity.matches ? GOOD_FG : BAD_FG,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {report.parity.matches
                  ? t(
                      'Matches the till’s own totals ✓',
                      'Matches the till’s own totals ✓',
                    )
                  : t(
                      'Differs from the till’s totals — some rows may not have synced',
                      'Differs from the till’s totals — some rows may not have synced',
                    )}
              </div>
            )}
            <div
              style={{
                padding: '16px 18px',
                borderRadius: 12,
                border: `1px solid ${CARD_BORDER}`,
                background: '#F8FAFA',
              }}
            >
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: INK,
                  marginBottom: 6,
                }}
              >
                {mode === 'x'
                  ? t('What this X covers', 'What this X covers')
                  : t('What this Z covers', 'What this Z covers')}
              </p>
              <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>
                {mode === 'x'
                  ? t(
                      'Everything this user rang up in this shift. Same maths, same lines as the X the till prints.',
                      'Everything this user rang up in this shift. Same maths, same lines as the X the till prints.',
                    )
                  : t(
                      'The whole till session — every user who sold on it since it opened.',
                      'The whole till session — every user who sold on it since it opened.',
                    )}
              </p>
            </div>
            <div
              style={{
                padding: '16px 18px',
                borderRadius: 12,
                border: `1px solid ${CARD_BORDER}`,
                background: '#FFFFFF',
              }}
            >
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: INK,
                  marginBottom: 4,
                }}
              >
                {t('Quick figures', 'Quick figures')}
              </p>
              {[
                [
                  report.open
                    ? t('Cash to declare', 'Cash to declare')
                    : t('System cash', 'System cash'),
                  formatMoney(report.cashDeclaration.systemAmount),
                ],
                [t('Opened', 'Opened'), formatTime(report.startDate)],
                [
                  t('Status', 'Status'),
                  report.open ? t('Open', 'Open') : t('Closed', 'Closed'),
                ],
              ].map(([label, value]) => (
                <div
                  key={String(label)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 13,
                    padding: '7px 0',
                    borderBottom: `1px solid ${CARD_BORDER}`,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <span style={{ color: MUTED }}>{label}</span>
                  <span style={{ color: INK, fontWeight: 600 }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Torn-edge + skeleton pulse, local so we don't touch the host CSS. */}
      <style>{`
        .cashup-tear {
          height: 12px;
          filter: drop-shadow(0 1px 0 rgba(15, 41, 42, 0.04));
          background:
            linear-gradient(45deg, transparent 33.333%, #FFFFFF 33.333%, #FFFFFF 66.667%, transparent 66.667%),
            linear-gradient(-45deg, transparent 33.333%, #FFFFFF 33.333%, #FFFFFF 66.667%, transparent 66.667%);
          background-size: 16px 24px;
          background-position: 0 -12px;
        }
        @keyframes cashup-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
        .cashup-pulse { animation: cashup-pulse 1.4s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default CashupXZReport;
